import React, { useEffect, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTimes,
  faArrowUp,
  faArrowDown,
  faPlus,
  faMinus,
} from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-toastify";
import { searchStock, createOrder } from "../../api/user";
import useLivePrice from "../../hooks/useLivePrice";
import { pairSymbol } from "../../utils/instrumentFormat";
import { formatOrderPrice, formatTotal, quantityLabel } from "../../utils/orderFormat";
import ForexLotKeypad from "../chart/ForexLotKeypad";
import ProcessingButton from "../Skeleton/ProcessingButton";
import OrderTicketSkeleton from "../Skeleton/OrderTicketSkeleton";

// Maps the order endpoint's existing refusal messages (lang/en/message.php
// STOCK.*, ORDER.ORDER_STATUS, LeagueAssets) to the client's launch copy.
const orderErrorText = (message) => {
  const m = String(message || "");
  if (m.startsWith("You have insufficient coins")) return "Not enough free cash for this order.";
  if (m.startsWith("You have insufficient holdings quantity")) return "Not enough available quantity for this order.";
  if (m.startsWith("You have insufficient short sell quantity")) return "Not enough available quantity for this order.";
  if (m === "The order has already been executed or closed.") return "This order is no longer open.";
  if (m === "This league has not started yet") return "Trading has not started in this league.";
  if (m === "This league has ended") return "This league has ended.";
  if (m === "Not eligible in this league") return "This asset is not eligible for this league.";
  return "Could not place the order. Try again.";
};

const getAssetType = (ticker) => {
  if (!ticker) return "stock";
  if (ticker.startsWith("C:")) return "forex";
  if (ticker.startsWith("X:")) return "crypto";
  return "stock";
};

const categoryFromSymbol = (sym) =>
  sym?.startsWith("C:") ? "Forex" : sym?.startsWith("X:") ? "Crypto" : "Stocks";

const positionTypeToTab = (pt) => {
  const n = Number(pt);
  if (n === 3) return "stopLoss";
  if (n === 1) return "market";
  return "limit";
};

// Same UI as the buy/sell bottom sheet inside MobileChartToolbar, but
// standalone so it can be opened on the portfolio page (or anywhere) without
// navigating to /charts. Used for the mobile "Edit Order" flow.
// `gameId` — the league this sheet is acting inside, passed by the portfolio that
// mounts it. NOT read from LeagueTradeContext: the league portfolio is not
// wrapped in that provider (only the Trade screen is), so the context would be
// null here and every order would silently book to the main account.
const MobileEditOrderSheet = ({ open, onClose, editOrder, onDone, gameId = null }) => {
  const isEditMode = !!editOrder?.id;
  const symbol = editOrder?.stock_symbol;

  const [loading, setLoading] = useState(false);
  const [stockData, setStockData] = useState(null);
  const [tradeType, setTradeType] = useState(1);
  const [activeOrderTab, setActiveOrderTab] = useState("limit");
  const [quantity, setQuantity] = useState(0);
  // Forex Units keypad: lot size (units/lot) + lot count; quantity = size × count.
  const [forexLotSize, setForexLotSize] = useState(null);
  const [forexLotCount, setForexLotCount] = useState(1);
  const [limitPrice, setLimitPrice] = useState(0);
  const [lossPrice, setLossPrice] = useState(0);
  const [marketPrice, setMarketPrice] = useState(0);
  const [placing, setPlacing] = useState(false);
  const [showQuantityPopup, setShowQuantityPopup] = useState(false);

  // Fetch stock data + prefill from editOrder whenever the sheet is opened
  // for a new order.
  useEffect(() => {
    if (!open || !symbol) return;
    let cancelled = false;
    setLoading(true);
    setShowQuantityPopup(false);
    (async () => {
      try {
        const res = await searchStock({
          search: symbol,
          category: categoryFromSymbol(symbol),
        });
        if (cancelled) return;
        if (res?.data?.status === 200) {
          const data = res.data.data;
          setStockData(data);
          // Fresh symbol/order → clear any prior forex lot selection.
          setForexLotSize(null);
          setForexLotCount(1);
          const price = data?.min?.c || data?.prevDay?.c || 0;
          const assetType = getAssetType(data?.ticker);
          const formatted = formatOrderPrice(price, assetType);
          if (isEditMode) {
            const editPrice = Number(editOrder.order_price);
            const editFormatted = Number.isFinite(editPrice)
              ? formatOrderPrice(editPrice, assetType)
              : formatted;
            const tab = positionTypeToTab(editOrder.postion_type);
            setLimitPrice(tab === "stopLoss" ? formatted : editFormatted);
            setLossPrice(tab === "stopLoss" ? editFormatted : formatted);
            setMarketPrice(formatted);
            setQuantity(Number(editOrder.order_quantity) || 0);
            setActiveOrderTab(tab);
            // A short is order_type 3 (a sell to open). Treat both 2 (sell) and
            // 3 (short) as the Sell side so modifying a pending short/sell order
            // doesn't pre-fill as Buy and silently flip the side on update.
            setTradeType(
              Number(editOrder.order_type) === 2 ||
                Number(editOrder.order_type) === 3
                ? 2
                : 1
            );
          } else if (editOrder?.isHolding) {
            // Holdings buy/sell: price = current market, qty = held shares,
            // side = closing direction (long → Sell, short → Buy).
            setLimitPrice(formatted);
            setLossPrice(formatted);
            setMarketPrice(formatted);
            setQuantity(Number(editOrder.order_quantity) || 0);
            setActiveOrderTab(editOrder?.defaultStopLoss ? "stopLoss" : "limit");
            const ot = Number(editOrder.order_type);
            setTradeType(ot === 2 || ot === 3 ? 1 : 2);
          } else {
            setLimitPrice(formatted);
            setLossPrice(formatted);
            setMarketPrice(formatted);
            setQuantity(0);
            setActiveOrderTab("limit");
            setTradeType(1);
          }
        }
      } catch {
        if (!cancelled) setStockData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, symbol, isEditMode, editOrder]);

  // Live-price subscription for the "Market" tab. The chart page broadcasts
  // `livePriceUpdate` window events while it's mounted, but this sheet runs on
  // the portfolio page where char.jsx is unmounted — so we open our own
  // Polygon FMV socket via useLivePrice.
  const liveTicker = open
    ? stockData?.ticker || editOrder?.stock_symbol || null
    : null;
  const livePrice = useLivePrice(liveTicker);
  useEffect(() => {
    if (!open || livePrice == null) return;
    const assetType = getAssetType(stockData?.ticker);
    setMarketPrice(formatOrderPrice(livePrice, assetType));
  }, [livePrice, open, stockData?.ticker]);

  // Swipe-down to close
  const swipeRef = useRef({ startY: null, currentY: null, dragging: false });
  const [swipeOffset, setSwipeOffset] = useState(0);
  const onTouchStart = (e) => {
    swipeRef.current = {
      startY: e.touches[0].clientY,
      currentY: e.touches[0].clientY,
      dragging: true,
    };
  };
  const onTouchMove = (e) => {
    const s = swipeRef.current;
    if (!s.dragging) return;
    const y = e.touches[0].clientY;
    s.currentY = y;
    const offset = Math.max(0, y - s.startY);
    setSwipeOffset(offset);
  };
  const onTouchEnd = () => {
    const s = swipeRef.current;
    const distance = (s.currentY ?? 0) - (s.startY ?? 0);
    swipeRef.current = { startY: null, currentY: null, dragging: false };
    setSwipeOffset(0);
    if (distance > 30) onClose && onClose();
  };

  const determineValue = () => {
    if (activeOrderTab === "market") {
      return marketPrice || stockData?.min?.c || stockData?.prevDay?.c || 0;
    }
    return activeOrderTab === "stopLoss" ? lossPrice : limitPrice;
  };

  const incrementQuantity = () => setQuantity((p) => Number(p) + 1);
  const decrementQuantity = () =>
    setQuantity((p) => (Number(p) > 0 ? Number(p) - 1 : 0));

  // Forex Units keypad handlers (Units = lot size × lot count).
  const forexSelectLot = (units) => {
    setForexLotSize(units);
    setForexLotCount(1);
    setQuantity(units);
  };
  const forexIncrement = () => {
    if (!forexLotSize) return;
    const n = forexLotCount + 1;
    setForexLotCount(n);
    setQuantity(forexLotSize * n);
  };
  const forexDecrement = () => {
    if (!forexLotSize) return;
    const n = Math.max(1, forexLotCount - 1);
    setForexLotCount(n);
    setQuantity(forexLotSize * n);
  };
  const forexClear = () => {
    setForexLotSize(null);
    setForexLotCount(1);
    setQuantity(0);
  };

  const handlePlaceOrder = async () => {
    if (!stockData?.ticker) {
      toast.error("Price data is unavailable. Try again.");
      return;
    }
    if (!quantity || Number(quantity) === 0) {
      toast.error("Please enter quantity");
      return;
    }
    setPlacing(true);
    try {
      const data = {
        stock_symbol: stockData.ticker,
        asset_type: getAssetType(stockData.ticker),
        order_type: tradeType,
        order_quantity: quantity,
        order_price: determineValue(),
        postion_type:
          activeOrderTab === "stopLoss"
            ? 3
            : activeOrderTab === "limit"
            ? 2
            : 1,
      };
      if (isEditMode) data.id = editOrder.id;
      // Closing a position or setting a stop INSIDE a league has to stay in that
      // league's book. Null outside one, where the payload is unchanged.
      if (gameId) data.game_id = gameId;
      const response = await createOrder(data);
      if (response?.data?.status === 200) {
        toast.success(isEditMode ? "Order updated." : "Order placed.");
        onDone && onDone();
      } else {
        toast.error(orderErrorText(response?.data?.message));
      }
    } catch (e) {
      if (e?.response) {
        // Server answered and rejected it — a confirmed failure.
        toast.error(orderErrorText(e.response?.data?.message));
      } else {
        // No response: the order may or may not have been created. NO retry
        // affordance — resubmitting could duplicate the order.
        toast.error(
          "We could not confirm this order. Check your portfolio before trying again."
        );
      }
    } finally {
      setPlacing(false);
    }
  };

  if (!open) return null;

  const assetType = getAssetType(stockData?.ticker);
  const totalPrice =
    Number(quantity) > 0
      ? formatTotal(determineValue() * Number(quantity))
      : formatTotal(determineValue());

  return (
    <div
      className="fixed inset-0 z-[60] flex items-end justify-center bg-black/70"
      data-no-chart-tap="true"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-t-2xl w-full max-w-lg p-4 shadow-2xl max-h-[85vh] overflow-visible"
        onClick={(e) => {
          e.stopPropagation();
          if (showQuantityPopup) setShowQuantityPopup(false);
        }}
        style={{
          minHeight: "480px",
          transform: `translateY(${swipeOffset}px)`,
          transition: swipeRef.current.dragging
            ? "none"
            : "transform 0.3s ease-out",
        }}
      >
        <div
          className="flex justify-center mb-3 cursor-grab py-2 -mt-2"
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
        >
          <div className="w-10 h-1 bg-gray-300 rounded-full" />
        </div>

        <div className="flex justify-between items-center mb-4">
          <h3 className="text-gray-800 text-lg font-semibold">
            {pairSymbol(stockData?.ticker || symbol) || "Trade"}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <FontAwesomeIcon icon={faTimes} className="w-5 h-5" />
          </button>
        </div>

        {loading ? (
          <OrderTicketSkeleton />
        ) : (
          <div className="space-y-4">
            <div>
              <p className="text-gray-800 mb-2">Trade Type</p>
              <div className="flex gap-3">
                <button
                  onClick={() => setTradeType(2)}
                  className={`flex-1 h-12 rounded-lg font-semibold text-lg flex items-center justify-center gap-1 ${
                    tradeType === 2
                      ? "bg-[#fe5b5e] text-white"
                      : "text-gray-800 bg-gray-200 hover:bg-gray-300"
                  }`}
                >
                  <FontAwesomeIcon
                    icon={faArrowDown}
                    className={`w-6 h-6 -rotate-45 ${
                      tradeType === 2 ? "text-white" : "text-gray-800"
                    }`}
                  />
                  Sell
                </button>
                <button
                  onClick={() => setTradeType(1)}
                  className={`flex-1 h-12 rounded-lg font-semibold text-lg flex items-center justify-center gap-1 ${
                    tradeType === 1
                      ? "bg-[#01cea7] text-white"
                      : "text-gray-800 bg-gray-200 hover:bg-gray-300"
                  }`}
                >
                  <FontAwesomeIcon
                    icon={faArrowUp}
                    className={`w-6 h-6 rotate-45 ${
                      tradeType === 1 ? "text-white" : "text-gray-800"
                    }`}
                  />
                  Buy
                </button>
              </div>
            </div>

            <div className="flex justify-center">
              {[
                { key: "limit", label: "Limit", radius: "rounded-l-lg" },
                { key: "market", label: "Market", radius: "" },
                { key: "stopLoss", label: "Stop Loss", radius: "rounded-r-lg" },
              ].map((t) => (
                <div
                  key={t.key}
                  onClick={() => setActiveOrderTab(t.key)}
                  className={`cursor-pointer py-2 px-4 border border-[#004c50] flex-1 text-center ${t.radius} ${
                    activeOrderTab === t.key
                      ? "bg-[#004c50] text-white"
                      : "bg-white text-[#004c50]"
                  }`}
                >
                  {t.label}
                </div>
              ))}
            </div>

            <div className="grid grid-cols-3 gap-2">
              <div className="relative">
                <p className="text-gray-800 mb-2 text-sm">{quantityLabel(assetType)}</p>
                <div
                  className="flex items-center justify-between px-3 py-2 bg-gray-200 rounded-lg cursor-pointer border border-transparent hover:border-[#004c50]"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowQuantityPopup((v) => !v);
                  }}
                >
                  <span className="text-gray-800 font-medium flex-1 text-center">
                    {quantity}
                  </span>
                  <span className="text-gray-500 text-xs">+/-</span>
                </div>
                {showQuantityPopup && (
                  <div
                    className="absolute left-0 mt-2 bg-[#1a1a2e] rounded-xl p-4 shadow-2xl z-50 w-40 bottom-full mb-2 overflow-y-auto h-52"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {getAssetType(stockData?.ticker) === "forex" ? (
                      <ForexLotKeypad
                        lotSize={forexLotSize}
                        onSelectLot={forexSelectLot}
                        onIncrement={forexIncrement}
                        onDecrement={forexDecrement}
                        onClear={forexClear}
                      />
                    ) : (
                      <>
                    <div className="grid grid-cols-2 gap-2 mb-2">
                      <button
                        onClick={decrementQuantity}
                        className="bg-[#2a2a3e] hover:bg-[#3a3a4e] text-white py-2 rounded-lg"
                      >
                        <FontAwesomeIcon icon={faMinus} className="w-4 h-4" />
                      </button>
                      <button
                        onClick={incrementQuantity}
                        className="bg-[#2a2a3e] hover:bg-[#3a3a4e] text-white py-2 rounded-lg"
                      >
                        <FontAwesomeIcon icon={faPlus} className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="grid grid-cols-2 gap-2 pb-1">
                      {[10, 25, 100, 500, 1000].map((v) => (
                        <button
                          key={v}
                          onClick={() => setQuantity((p) => Number(p) + v)}
                          className="bg-[#2a2a3e] hover:bg-[#3a3a4e] text-white py-2 rounded-lg text-sm font-medium"
                        >
                          {v}
                        </button>
                      ))}
                      <button
                        onClick={() => setQuantity(0)}
                        className="bg-[#2a2a3e] hover:bg-red-600 text-white py-2 rounded-lg text-sm font-medium"
                      >
                        C
                      </button>
                    </div>
                      </>
                    )}
                  </div>
                )}
              </div>

              <div>
                <p className="text-gray-800 mb-2 text-sm">Price</p>
                <div className="flex items-center px-2 py-2 bg-gray-200 rounded-lg">
                  <input
                    type="text"
                    value={determineValue()}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (/^\d*\.?\d{0,6}$/.test(val)) {
                        activeOrderTab === "limit"
                          ? setLimitPrice(val)
                          : setLossPrice(val);
                      }
                    }}
                    disabled={activeOrderTab === "market"}
                    className="w-full text-center bg-transparent border-none outline-none text-gray-800"
                  />
                </div>
              </div>

              <div>
                <p className="text-gray-800 mb-2 text-sm">Total</p>
                <div className="flex items-center px-2 py-2 bg-gray-200 rounded-lg">
                  <input
                    type="text"
                    value={totalPrice}
                    readOnly
                    className="w-full text-center bg-transparent border-none outline-none text-gray-800"
                  />
                </div>
              </div>
            </div>

            <ProcessingButton
              className="w-full h-12 rounded-full bg-[#004c50] text-white font-semibold shadow-lg hover:bg-[#003a3d] transition-colors"
              onClick={handlePlaceOrder}
              processing={placing}
              processingLabel={isEditMode ? "Updating" : "Placing"}
              label={isEditMode ? "Update Order" : "Place Order"}
            />

            <div className="py-2 text-center text-black/60">
              <p className="text-[12px] leading-tight whitespace-nowrap">
                This is live simulated trading with virtual cash.
              </p>
              <p className="text-[11px] leading-tight whitespace-nowrap">
                No brokerage order is sent, and nothing here is financial advice.
              </p>
              <p className="text-[11px] leading-tight whitespace-nowrap">
                Learn in real time without risking real money.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MobileEditOrderSheet;
